/**
 * Represents a customizable section in a Shopify storefront that interacts
 * with a backend service to progressively reveal options based on previous selections.
 * This is particularly useful for building dynamic product configurations or filters.
 */
class MbSection extends HTMLElement {
  /**
   * Initializes the component with default values and setups the initial context.
   */
  constructor() {
    super();
    this.shopifyUrl = window.location.origin;
    this.startContextString = this.getAttribute('data-start-mb-context');
    this.start_context = JSON.parse(this.startContextString);
    this.currentContext = {...this.start_context};  // Use spread to clone the context
    this.min_question = parseInt(this.getAttribute('data-question-min'));
    this.max_question = parseInt(this.getAttribute('data-question-max'));
    this.resetButton = this.querySelector('#mb_reset');
    this.apiKey = this.getAttribute('data-api-key');

    // Bind methods
    this.handleSelectChange = this.handleSelectChange.bind(this);
    this.handleSearchButtonClick = this.handleSearchButtonClick.bind(this);
    this.handleResetButtonClick = this.handleResetButtonClick.bind(this);
  }

  /**
   * Lifecycle method called when the element is added to the document.
   * Sets up event listeners and triggers the start method.
   */
  connectedCallback() {
    this.addEventListener('change', this.handleSelectChange);
    this.start();
    this.setupSearchButtonListener();
    this.setupResetButtonListener();

    // Initially hide the reset button
    if (this.resetButton) {
      this.resetButton.style.display = 'none';
    }
  }

  /**
   * Sends a POST request to a predefined URL with the current context
   * to fetch the next set of options.
   * @param {Object} body The payload to be sent with the request.
   * @returns {Promise<Object>} The response context from the API.
   */
  async sendRequest(context) {
    const mbHeaders = new Headers();
    mbHeaders.append("Content-Type", "application/json");
    mbHeaders.append("X-Target-Endpoint", "mb-dropdowns-v1");

    const requestOptions = {
      method: 'POST',
      headers: mbHeaders,
      body: JSON.stringify(context),
    };

    try {
      const proxyUrl = '/apps/proxy';
      const response = await fetch(proxyUrl, requestOptions);

      // Only log deprecation warning if the header exists and is true
      if (response.headers.get('X-API-Deprecated') === 'true') {
        console.warn('API Deprecation Warning:', {
          version: response.headers.get('X-API-Version'),
          sunsetDate: response.headers.get('X-API-Sunset'),
          message: response.headers.get('X-API-Deprecation-Message')
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const jsonResponse = await response.json();
      return jsonResponse;
    } catch (error) {
      console.error('Error making request or parsing JSON:', error);
      throw error;
    }
  }

  // async sendRequest(context) {
  //   // console.log('Sending request with body:', context);
  //   const mbHeaders = new Headers();
  //   mbHeaders.append("Content-Type", "application/json");

  //   const requestOptions = {
  //     method: 'POST',
  //     headers: mbHeaders,
  //     body: JSON.stringify(context),
  //   };


  //   const baseUrl = this.shopifyUrl === "https://rammount-development.myshopify.com"
  //   ? "https://shopify-mount-builder-staging.rammount.com/dropdown_next_steps?key=gNz!W%232Pyeu8RdKGzA4%25SWrr"
  //   : "https://shopify-mount-builder.rammount.com/dropdown_next_steps?key=gNz!W%232Pyeu8RdKGzA4%25SWrr";

  //   // const baseUrl = "https://shopify-mount-builder-staging.rammount.com/dropdown_next_steps?key=gNz!W%232Pyeu8RdKGzA4%25SWrr";

  //   try {
  //     const response = await fetch(baseUrl, requestOptions);

  //     if (!response.ok) {
  //         throw new Error(`HTTP error! status: ${response.status}`);
  //     }
  //     const jsonResponse = await response.json();
  //     return jsonResponse;
  //   } catch (error) {
  //     console.error('Error making request or parsing JSON:', error);
  //   }
  // }

  /**
   * Initiates the component by making the first API request based on the starting context.
   */
  async start() {
    let sequenceNumber = 1;
    const initialRequestBody = this.start_context
    const result = await this.sendRequest(initialRequestBody);

    if (result) {
      this.populateDropdown(result, sequenceNumber);
    }
  }

  /**
   * Fills a dropdown with options based on the data received from the API.
   * @param {Object} data The data containing options to populate the dropdown.
   * @param {number} sequenceNumber The sequence number of the dropdown to be populated.
   */
  populateDropdown(data, sequenceNumber) {
    // Extract relevant fields from the data object.
    const { field_label, context_question, option_values } = data;

    // Find the select element and label corresponding to the sequence number.
    const select = this.querySelector(`[data-select="${sequenceNumber}"]`);
    const label = this.querySelector(`[data-label="${sequenceNumber}"]`);

    // Update the select element and label with the new options.
    if (select) {
        select.setAttribute('data-context-question', context_question);

        // Generate a unique id for the select element using the context_question
        const selectId = `dropdown-${context_question}`;
        select.setAttribute('id', selectId);

        // Dynamically assign an aria-labelledby attribute based on context_question
        select.setAttribute('aria-labelledby', `label-${context_question}`);
    }

    if (label && select && option_values) {
        // Set the label text and associate it with the select element
        label.textContent = field_label;
        label.setAttribute('for', select.getAttribute('id'));
        label.setAttribute('id', `label-${context_question}`);

        // If you set an aria-label before, remove or override it
        select.removeAttribute('aria-label');

        select.innerHTML = ''; // Clear any existing options

        // Create a default option
        const defaultOption = document.createElement('option');
        defaultOption.value = "";
        defaultOption.selected = true;
        defaultOption.textContent = `Select ${field_label}`;
        select.appendChild(defaultOption);

        // Populate the dropdown with options
        option_values.forEach(value => {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = value;
            select.appendChild(option);
        });

        select.disabled = false;
    }
  }

  /**
   * Handles changes to any of the dropdowns, updating the context and
   * fetching the next set of options accordingly.
   * @param {Event} event The change event from the select element.
   */
  async handleSelectChange(event) {
    if (event.target.matches('[data-select]')) {
      const selectedValue = event.target.value;

      // Show reset button if a dropdown is selected
      if (selectedValue !== "" && this.resetButton) {
        this.resetButton.style.display = 'block';
      }

      const contextQuestion = event.target.getAttribute('data-context-question');
      const currentSequenceNumber = parseInt(event.target.getAttribute('data-select'), 10);

      // Reset subsequent dropdowns and clear context entries affected by the change
      this.resetSubsequentDropdowns(currentSequenceNumber);

      // Update context based on the selection
      if (selectedValue === "") {
        delete this.currentContext[contextQuestion];
        // Additional clean-up if necessary (specific business logic here)
        if (contextQuestion === "device") { // Clear related context if the device is cleared or changed
          delete this.currentContext['vehicle']; // Clear vehicle if the device is cleared
        }
      } else {
        this.currentContext[contextQuestion] = selectedValue;
        // Clear related context if necessary (specific business logic here)
        if (contextQuestion === "device" && selectedValue !== "Laptop") {
          delete this.currentContext['vehicle']; // Clear vehicle if the device is not Laptop
        }
      }

      // Recreate a new context object based on the remaining valid context
      const newContext = {...this.currentContext};

      // Update context for all previous selections up to current
      const allSelects = this.querySelectorAll('[data-select]');
      allSelects.forEach((select) => {
        const selectSequence = parseInt(select.getAttribute('data-select'), 10);
        if (selectSequence < currentSequenceNumber) {
          const selectContextQuestion = select.getAttribute('data-context-question');
          if (select.value) {
            newContext[selectContextQuestion] = select.value;
          }
        }
      });

      // Send updated context to server if not resetting to default
      if (selectedValue !== "") {
        const result = await this.sendRequest(newContext);
        if (result) {
          // Update the global context with the response
          this.currentContext = {...newContext, ...result.context, extra_url: result.extra_url};
          const nextSequenceNumber = currentSequenceNumber + 1;

          if (result.page_type === "results") {
            //console.log('Received results, no further dropdowns to populate.');
          } else {
            // Populate next dropdown if within the specified question range
            if (
              nextSequenceNumber <= this.min_question ||
              (nextSequenceNumber <= this.max_question && (result.next_level_has_url || !this.currentContext.extra_url))
            ) {
              this.populateDropdown(result, nextSequenceNumber);
            }
          }
        }
      }
    }
  }

  /**
   * Resets dropdowns that follow the currently updated one to their default states.
   * @param {number} startingSequence The sequence number from which to start resetting dropdowns.
   */
  resetSubsequentDropdowns(startingSequence) {
    const allSelects = this.querySelectorAll('[data-select]');
    allSelects.forEach((select, index) => {
      const sequenceNumber = index + 1;
      if (startingSequence === 0 || sequenceNumber > startingSequence) {
        select.innerHTML = '<option value="" disabled selected></option>'; // Reset to a default state
        select.disabled = true; // Disable the select
        const label = this.querySelector(`[data-label="${sequenceNumber}"]`);
        if (label) {
          // Provide a short descriptor for screen readers
          label.textContent = 'Disabled until previous selection is made.';
        }

        // If you want a short aria-label on the <select> itself (optional):
        select.setAttribute('aria-label', 'Disabled until previous selection is made.');

        const contextQuestion = select.getAttribute('data-context-question');
        if (contextQuestion) {
          delete this.currentContext[contextQuestion]; // Remove context entry
        }
      }
    });
  }

  handleSearchButtonClick() {
    // Check if IntelliSkin is selected as the case type
    if (this.currentContext.case_type === 'IntelliSkin®' ||
        (this.currentContext.case_type && this.currentContext.case_type.toLowerCase().includes('intelliskin'))) {

      // Extract device info from current context
      const deviceMake = this.currentContext.device_make;
      const deviceModel = this.currentContext.device_model;

      // Only redirect if we have both make and model
      if (deviceMake && deviceModel) {
        // Format for GDS builder
        const gdsContext = {
          "Device Make": deviceMake,
          "Device Model": deviceModel
        };

        // Redirect to GDS builder with context
        const queryParams = new URLSearchParams({
          context: JSON.stringify(gdsContext)
        });

        window.location.href = `${this.shopifyUrl}/pages/gds-builder?${queryParams.toString()}`;
        return; // Exit early to prevent normal flow
      }
    }

    // Normal flow for non-IntelliSkin options
    if (this.currentContext && this.currentContext.extra_url && this.currentContext.extra_url.length > 0) {
      window.location.href = this.shopifyUrl + this.currentContext.extra_url[0];
    } else {
      const queryParams = new URLSearchParams(this.currentContext).toString();
      const mountBuilderUrl = `${this.shopifyUrl}/pages/mount-builder?${queryParams}`;
      window.location.href = mountBuilderUrl;
    }
  }

  setupSearchButtonListener() {
    const searchButton = this.querySelector('#mb_search');
    searchButton.addEventListener('click', this.handleSearchButtonClick);
  }

  handleResetButtonClick() {
    // console.log('Reset button clicked.');

    // Reset the current context to the initial state
    this.currentContext = { ...this.start_context };

    // Reset all dropdowns using resetSubsequentDropdowns
    this.resetSubsequentDropdowns(0); // Reset all dropdowns

    // Restart the initialization process
    // console.log('Dropdowns reset. Restarting initialization.');
    this.start();

    // Hide the reset button
    if (this.resetButton) {
      this.resetButton.style.display = 'none';
    }
  }

  setupResetButtonListener() {
    const resetButton = this.querySelector('#mb_reset');
    if (resetButton) {
      resetButton.addEventListener('click', this.handleResetButtonClick);
    }
  }
}

// Defines the custom element.
customElements.define('mb-section', MbSection);
